import { Component, DestroyRef, ViewChild, computed, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BankKitRequestDraftResponse } from '@interfaces/partners/responses/bank-kit-request.response';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { BankKitRequestHttpService } from '@services/http/partners/bank-kit-request-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { describeClientError } from '@src/app/shared/utils/client-error-message';
import { Subject, debounceTime, of, switchMap, catchError } from 'rxjs';
import {
  BankKitRecipientOption,
  addManualRecipient,
  buildRecipientOptions,
  describeRecipientSource,
} from './bank-kit-recipients';

/** Espera depois da última tecla antes de pedir a pré-visualização ao backend. */
const PREVIEW_DEBOUNCE_MS = 600;

/**
 * Modal "Solicitar Kit Banco" (card 3753), aberta pela aba Documentos do deal.
 *
 * Abre com o assunto e a mensagem padrão do backend (lista de documentos das regras de completude do Data Room), os
 * contatos e usuários da empresa como sugestão de destinatário e, à direita, o e-mail **montado pelo backend** como vai
 * sair (mesmo layout e mesmo texto do envio). A mensagem é texto: linhas com "- " viram lista.
 *
 * O e-mail sai com Reply-To no endereço da empresa na caixa de entrada do Data Room: a resposta do cliente com os
 * anexos entra na `_TRIAGEM` da empresa, onde o Cog identifica e guarda cada documento.
 *
 * ⚠️ Botões do rodapé são `type="button"` com `(sendButtonClick)`: `type="submit"` dentro de form chama o handler duas
 * vezes (bug conhecido do `app-button`).
 */
@Component({
  selector: 'app-modal-bank-kit-request',
  standalone: true,
  templateUrl: './modal-bank-kit-request.component.html',
  styleUrl: './modal-bank-kit-request.component.scss',
  imports: [ReactiveFormsModule, SharedModule],
})
export class ModalBankKitRequestComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  private readonly formBuilder = inject(FormBuilder);
  private readonly bankKitService = inject(BankKitRequestHttpService);
  private readonly toast = inject(ToastHelperService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);

  /** Avisa o pai que ao menos um e-mail saiu, para a tela do deal recarregar a linha do tempo. */
  readonly requested = output<void>();

  /** Carregando o rascunho. */
  readonly loadingDraft = signal(false);

  /** Falha ao carregar o rascunho (mensagem do backend ou da tela). */
  readonly draftError = signal<string | null>(null);

  /** Rascunho do backend (endereço de resposta, texto padrão e limite). */
  readonly draft = signal<BankKitRequestDraftResponse | null>(null);

  /** Destinatários (sugeridos e digitados), com a marcação de quem recebe. */
  readonly recipients = signal<BankKitRecipientOption[]>([]);

  /** Erro do e-mail digitado em "Adicionar". */
  readonly manualEmailError = signal<string | null>(null);

  /** Assunto final da pré-visualização (com o prefixo de teste fora de produção). */
  readonly previewSubject = signal<string | null>(null);

  /** HTML da pré-visualização, liberado para o `srcdoc` do iframe (sandbox sem script). */
  readonly previewHtml = signal<SafeHtml | null>(null);

  /** Pedindo a pré-visualização. */
  readonly loadingPreview = signal(false);

  /** Enviando. Nome próprio: o `isLoading` do `VaultBaseComponent` é o spinner global. */
  readonly sending = signal(false);

  readonly selectedRecipients = computed(() => this.recipients().filter(r => r.selected));

  readonly title = signal('Solicitar Kit Banco');

  readonly describeSource = describeRecipientSource;

  form: FormGroup = this.formBuilder.group({
    subject: ['', [Validators.required, Validators.maxLength(200)]],
    message: ['', [Validators.required, Validators.maxLength(8000)]],
    manualEmail: [''],
  });

  private dealId: string | null = null;
  private readonly previewRequests = new Subject<void>();

  constructor(translateService: TranslateService) {
    super(translateService);

    this.previewRequests
      .pipe(
        debounceTime(PREVIEW_DEBOUNCE_MS),
        switchMap(() => {
          const request = this.buildPreviewRequest();
          if (!this.dealId || !request) return of(null);
          this.loadingPreview.set(true);
          return this.bankKitService.preview(this.dealId, request).pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(preview => {
        this.loadingPreview.set(false);
        if (!preview) return;
        this.previewSubject.set(preview.subject);
        this.previewHtml.set(this.sanitizer.bypassSecurityTrustHtml(preview.html));
      });

    this.form
      .get('subject')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.previewRequests.next());
    this.form
      .get('message')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.previewRequests.next());
  }

  /** Abre a modal e carrega o rascunho do deal. */
  open(dealId: string): void {
    this.dealId = dealId;
    this.resetState();
    this.dialogRef?.open('', '', '', 'Cancelar', '', 'bank-kit-request', false, true);
    this.loadDraft();
  }

  /** Recarrega o rascunho (botão "Tentar de novo"). */
  loadDraft(): void {
    if (!this.dealId) return;
    this.loadingDraft.set(true);
    this.draftError.set(null);
    this.bankKitService.getDraft(this.dealId).subscribe({
      next: draft => {
        this.loadingDraft.set(false);
        if (!draft) {
          this.draftError.set('Não recebemos o texto do pedido. Use Tentar de novo.');
          return;
        }
        this.draft.set(draft);
        this.title.set(
          draft.companyName ? `Solicitar Kit Banco: ${draft.companyName}` : 'Solicitar Kit Banco'
        );
        this.recipients.set(buildRecipientOptions(draft.suggestedRecipients));
        this.form.patchValue({ subject: draft.subject, message: draft.message, manualEmail: '' });
        this.previewRequests.next();
      },
      error: error => {
        this.loadingDraft.set(false);
        this.draftError.set(
          describeClientError(
            error,
            'Não foi possível abrir o pedido. Confira a sua conexão e use Tentar de novo.'
          )
        );
      },
    });
  }

  /** Marca ou desmarca um destinatário. */
  toggleRecipient(email: string): void {
    this.recipients.update(list =>
      list.map(r => (r.email === email ? { ...r, selected: !r.selected } : r))
    );
    this.previewRequests.next();
  }

  /** Acrescenta o e-mail digitado, já marcado. */
  addManualEmail(): void {
    const raw = this.form.get('manualEmail')?.value ?? '';
    const { options, error } = addManualRecipient(this.recipients(), raw);
    this.manualEmailError.set(error);
    if (error) return;
    this.recipients.set(options);
    this.form.patchValue({ manualEmail: '' });
    this.previewRequests.next();
  }

  /** Volta assunto e mensagem ao texto padrão do backend. */
  restoreDefaultText(): void {
    const draft = this.draft();
    if (!draft) return;
    this.form.patchValue({ subject: draft.subject, message: draft.message });
  }

  /** Assunto e mensagem preenchidos, pelo menos uma pessoa e dentro do limite. */
  isFormValid(): boolean {
    const subject = (this.form.get('subject')?.value ?? '').trim();
    const message = (this.form.get('message')?.value ?? '').trim();
    const count = this.selectedRecipients().length;
    const max = this.draft()?.maxRecipients ?? 10;
    return !!this.draft() && !!subject && !!message && count > 0 && count <= max;
  }

  /** Texto do botão de envio, com a contagem. */
  sendLabel(): string {
    const count = this.selectedRecipients().length;
    return count > 1 ? `Enviar pedido para ${count} pessoas` : 'Enviar pedido';
  }

  submitAction(): void {
    if (!this.dealId || !this.isFormValid() || this.sending()) return;

    this.sending.set(true);
    this.bankKitService
      .send(this.dealId, {
        subject: this.form.get('subject')?.value ?? '',
        message: this.form.get('message')?.value ?? '',
        recipients: this.selectedRecipients().map(r => ({
          name: r.name,
          email: r.email,
          source: r.source,
        })),
      })
      .subscribe({
        next: result => {
          this.sending.set(false);
          const deliveries = result?.deliveries ?? [];
          const failed = deliveries.filter(d => !d.sent);
          if (!result || result.sentCount === 0) {
            this.toast.showToastError(
              'O pedido não saiu',
              failed.map(d => `${d.email}: ${d.message}`).join(' ') ||
                'Nenhum e-mail foi enviado. Confira os destinatários e use Enviar pedido de novo.'
            );
            return;
          }

          this.toast.showToastSuccess(
            'Kit Banco solicitado',
            `Pedido enviado para ${result.sentCount} ${result.sentCount > 1 ? 'pessoas' : 'pessoa'}. As respostas com os documentos chegam em ${result.replyToAddress} e entram direto no Data Room da empresa.`
          );
          if (failed.length) {
            this.toast.showToastError(
              'Alguns e-mails não saíram',
              failed.map(d => `${d.email}: ${d.message}`).join(' ')
            );
          }
          this.requested.emit();
          this.dialogRef?.close();
        },
        error: error => {
          this.sending.set(false);
          this.toast.showToastError(
            'Não foi possível enviar o pedido',
            describeClientError(
              error,
              'Confira a sua conexão e use Enviar pedido de novo; se continuar assim, fale com o time da Legatus.'
            )
          );
        },
      });
  }

  /** Assunto, mensagem e o nome do primeiro destinatário marcado (saudação da pré-visualização). */
  private buildPreviewRequest() {
    const subject = (this.form.get('subject')?.value ?? '').trim();
    const message = this.form.get('message')?.value ?? '';
    if (!subject || !message.trim()) return null;
    return { subject, message, recipientName: this.selectedRecipients()[0]?.name ?? null };
  }

  /** Limpa o que ficou da abertura anterior. */
  private resetState(): void {
    this.draft.set(null);
    this.draftError.set(null);
    this.recipients.set([]);
    this.manualEmailError.set(null);
    this.previewHtml.set(null);
    this.previewSubject.set(null);
    this.sending.set(false);
    this.title.set('Solicitar Kit Banco');
    this.form.reset({ subject: '', message: '', manualEmail: '' });
  }
}
